import React, { useEffect, useState } from 'react';
import { Modal, Form, DatePicker, Input, Table, Alert, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCreateDeliveryFromWorkOrderBatch } from '@/hooks/useDelivery';
import type { DeliveryReadinessRow } from '@/services/delivery.service';

const { TextArea } = Input;
const { Text } = Typography;

interface CreateDeliveryFromReadinessModalProps {
  open: boolean;
  rows: DeliveryReadinessRow[];
  onClose: () => void;
  onSuccess?: (deliveryId: string, deliveryNumber: string) => void;
}

export const CreateDeliveryFromReadinessModal: React.FC<CreateDeliveryFromReadinessModalProps> = ({
  open,
  rows,
  onClose,
  onSuccess,
}) => {
  const [deliveryDate, setDeliveryDate] = useState(dayjs());
  const [customerPo, setCustomerPo] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [notes, setNotes] = useState('');
  const createMutation = useCreateDeliveryFromWorkOrderBatch();

  const salesOrderId = rows[0]?.sales_order_id ?? '';

  useEffect(() => {
    if (open && rows.length > 0) {
      setDeliveryAddress(rows[0].delivery_location ?? '');
      setDeliveryDate(dayjs());
      setCustomerPo('');
      setNotes('');
    }
  }, [open, rows]);

  const columns: ColumnsType<DeliveryReadinessRow> = [
    { title: 'WO', dataIndex: 'work_order_number', width: 110 },
    { title: 'Line', dataIndex: 'item_description', ellipsis: true },
    {
      title: 'Unit',
      dataIndex: 'production_unit_number',
      width: 70,
      render: (v: number | null) => (v != null ? `#${v}` : '—'),
    },
    { title: 'Qty', dataIndex: 'available_quantity', width: 60 },
  ];

  const handleSubmit = async () => {
    if (!salesOrderId) return;
    const delivery = await createMutation.mutateAsync({
      sales_order_id: salesOrderId,
      delivery_date: deliveryDate.format('YYYY-MM-DD'),
      customer_po_number: customerPo || undefined,
      delivery_address: deliveryAddress || undefined,
      notes: notes || undefined,
      items: rows.map((r) => ({
        work_order_id: r.work_order_id,
        work_order_line_item_id: r.work_order_line_item_id,
        delivered_quantity: r.available_quantity,
        production_unit_number: r.production_unit_number,
      })),
    });
    onSuccess?.(delivery.id, delivery.delivery_note_number);
    onClose();
  };

  return (
    <Modal
      title="Create delivery note"
      open={open}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={createMutation.isPending}
      okText="Create delivery note"
      width={720}
      destroyOnClose
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <Text>
            Creating one delivery note for customer order{' '}
            <Text strong>{rows[0]?.sales_order_number ?? '—'}</Text> with {rows.length} line(s).
          </Text>
        }
      />
      <Form layout="vertical">
        <Form.Item label="Delivery date">
          <DatePicker value={deliveryDate} onChange={(d) => d && setDeliveryDate(d)} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Customer PO">
          <Input value={customerPo} onChange={(e) => setCustomerPo(e.target.value)} />
        </Form.Item>
        <Form.Item label="Delivery address">
          <TextArea rows={2} value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} />
        </Form.Item>
        <Form.Item label="Notes">
          <TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Form.Item>
      </Form>
      <Table
        size="small"
        rowKey="row_key"
        pagination={false}
        dataSource={rows}
        columns={columns}
        scroll={{ y: 200 }}
      />
    </Modal>
  );
};
